// src/components/layout/mobile_Header.jsx
import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import { Bell, MessageSquare } from "lucide-react";
import AlertDropdown from "../../alerts/mobile_AlertDropdown";
import ChatPanel from "../../chat/ChatPanel";
import { setUnreadListener } from "../../../api/axios";
import MobileMenu from "../mobile/Menu";
import MobileSystemMenu from "../mobile/SystemMenu"; // 임포트 추가
import "../../alerts/AlertDropdown.css";

function MobileHeader({ username }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [spin, setSpin] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const projects = JSON.parse(localStorage.getItem("projects") || "[]");
  const projectId = localStorage.getItem("projectId");
  const isSelectProjectPage = location.pathname.startsWith("/select-project");

  const goDashboard = () =>
    projectId ? navigate(`/project/${projectId}`) : navigate("/select-project");

  const goSystem = () => {
    setSpin(true);
    navigate(
      projectId ? `/project/${projectId}/system/user` : "/select-project"
    );
    setTimeout(() => setSpin(false), 900);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const bellRef = useRef(null);
  const [ringBell, setRingBell] = useState(false);
  const prevUnreadCount = useRef(0);
  const isInitialized = useRef(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSystemMenuOpen, setIsSystemMenuOpen] = useState(false); // 시스템 메뉴 상태

  useEffect(() => {
    setUnreadListener((count) => {
      // 초기화 후에만 새 알림 체크
      if (isInitialized.current && count > prevUnreadCount.current) {
        setRingBell(true);
        setTimeout(() => setRingBell(false), 800);
      }
      prevUnreadCount.current = count;
      setUnreadCount(count);
      isInitialized.current = true;
    });
  }, []);

  // 메뉴 열림 상태에 따라 body 클래스 추가/제거
  useEffect(() => {
    if (isMenuOpen || isSystemMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }

    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [isMenuOpen, isSystemMenuOpen]);

  // 제목 & 프로젝트명
  const titleRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    if (!projectId) return;
    const match = projects.find((p) => String(p.id) === String(projectId));
    if (match) setProjectName(match.name);
  }, [projectId, projects]);

  useEffect(() => {
    if (!titleRef.current) return;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      if (w < 260) setIsCompact(true);
      else setIsCompact(false);
    });
    ro.observe(titleRef.current);

    return () => ro.disconnect();
  }, []);

  let titleText = "";
  if (!projectName) {
    titleText = isCompact ? "PMIS" : "Project Management Information System";
  }

  const closeMenu = () => {
    setIsMenuOpen(false);
    setTimeout(() => setIsSystemMenuOpen(false), 300); // 닫기 애니메이션 후 서브메뉴 상태도 리셋
  };

  return (
    <nav className="w-full bg-[#1e1e2f] text-white no-print relative z-40">
      {/* 상단 바 */}
      <div className="w-full pl-4 pr-6 py-3 flex items-center justify-between">
        {/* 왼쪽: 제목 / 프로젝트명 */}
        <button
          ref={titleRef}
          onClick={goDashboard}
          className="font-bold text-sm sm:text-base hover:opacity-90 text-left leading-tight max-w-[70vw]"
        >
          {projectName ? (
            <>
              <span className="block break-words">{projectName}</span>
              <span className="block break-words">PMIS</span>
            </>
          ) : (
            <span className="block break-words">{titleText}</span>
          )}
        </button>

        {/* 오른쪽: 알림 + 햄버거 */}
        <div className="ml-auto flex items-center gap-2">
          {projectId && (
            <button
              ref={bellRef}
              onClick={() => setOpen((prev) => !prev)}
              aria-label="알림"
              className="relative p-2 rounded-full hover:bg-gray-700"
            >
              <Bell size={20} className={ringBell ? "ring-bell" : ""} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-medium flex items-center justify-center min-w-[18px] h-[18px] rounded-full leading-none pulse-badge">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          )}

          {projectId && (
            <button
              onClick={() => setIsChatOpen(true)}
              aria-label="채팅"
              className="relative p-2 rounded-full hover:bg-gray-700 transition"
            >
              <MessageSquare size={20} />
              {chatUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] font-medium flex items-center justify-center min-w-[18px] h-[18px] rounded-full leading-none pulse-badge">
                  {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                </span>
              )}
            </button>
          )}

          {projectId && (
            <button
              type="button"
              className="flex flex-col justify-center items-center p-2 rounded-md hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/40"
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label="메인 메뉴 토글"
            >
              <span className="block w-5 h-0.5 bg-white mb-1" />
              <span className="block w-5 h-0.5 bg-white mb-1" />
              <span className="block w-5 h-0.5 bg-white" />
            </button>
          )}
        </div>
      </div>

      {/* 슬라이드 메뉴 영역 */}
      {projectId && (
        <>
          {/* 메뉴 배경 */}
          <div
            className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${isMenuOpen
              ? "opacity-100 pointer-events-auto"
              : "opacity-0 pointer-events-none"
              }`}
            onClick={closeMenu}
          />

          {/* 메뉴 패널 */}
          <div
            className={`fixed top-0 right-0 h-full w-45 max-w-[70%] bg-[#1e1e2f] text-white z-50 shadow-xl transform transition-transform duration-300 ease-out flex flex-col ${isMenuOpen ? "translate-x-0" : "translate-x-full"
              }`}
          >
            {isSystemMenuOpen ? (
              <MobileSystemMenu
                onBack={() => setIsSystemMenuOpen(false)}
                closeMenu={closeMenu}
              />
            ) : (
              <>
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-gray-300">로그인 사용자</span>
                    <span className="text-base font-semibold truncate">
                      {user?.name || username || "User"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={closeMenu}
                    aria-label="메뉴 닫기"
                    className="p-1 rounded-full hover:bg-white/10"
                  >
                    <span className="text-xl leading-none">&times;</span>
                  </button>
                </div>

                <div className="px-4 py-3 border-b border-white/10 space-y-2">
                  {projectId && (
                    <>
                      <button
                        onClick={() => setIsSystemMenuOpen(true)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/15 text-sm"
                      >
                        <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-white/10 text-xs font-semibold">
                          M
                        </span>
                        <span>마이페이지/설정</span>
                      </button>

                      <button
                        onClick={() => {
                          closeMenu();
                          goSystem();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/15 text-sm"
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/duck.ico`}
                          alt="시스템 관리"
                          className={`h-5 w-5 shrink-0 ${spin ? "animate-spin" : ""
                            }`}
                        />
                        <span>시스템 관리</span>
                      </button>
                    </>
                  )}

                  {projects.length > 1 && !isSelectProjectPage && (
                    <button
                      onClick={() => {
                        closeMenu();
                        localStorage.removeItem("projectId");
                        navigate("/select-project");
                      }}
                      className="w-full px-3 py-2 rounded-md bg-white/5 hover:bg-white/15 text-sm text-left"
                    >
                      프로젝트 변경
                    </button>
                  )}

                  <button
                    onClick={() => {
                      closeMenu();
                      handleLogout();
                    }}
                    className="w-full px-3 py-2 rounded-md bg-red-500/80 hover:bg-red-500 text-sm text-left"
                  >
                    로그아웃
                  </button>
                </div>

                {/* 하단 고정 바(결재 상신 버튼 등)를 고려하여 충분한 패딩 추가 */}
                <div className="flex-1 overflow-y-auto pb-24">
                  <MobileMenu isMenuOpen={isMenuOpen} onClose={closeMenu} />
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ✅ 알림 드롭다운 오버레이 (헤더 아래)
          background polling을 위해 항상 렌더링하고, isOpen으로 내부 표시 제어 */}
      {projectId && (
        <AlertDropdown
          isOpen={open}
          setOpen={setOpen}
          setUnreadCount={setUnreadCount}
          triggerRef={bellRef}
        />
      )}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setOpen(false)}
        />
      )}
      <ChatPanel
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        onOpenExternally={() => setIsChatOpen(true)}
        onUnreadCountChange={setChatUnreadCount}
      />
    </nav>
  );
}

export default MobileHeader;
